import { useMemo, useCallback } from 'react';
import { useCurrencies } from '../../../hooks/useCurrencies';
import { useBanks } from '../../../hooks/useBanks';
import { useSafes } from '../../../hooks/useSafes';
import { invoiceHiresService } from '../../../services';
import { DropdownOption } from './inTransactionTypes';

/**
 * Shared dropdown options and invoice search for the In Transaction form
 */
export function useInTransactionOptions() {
  const { data: currencies, isLoading: currenciesLoading } = useCurrencies({ populate: '*' });
  const { data: banks, isLoading: banksLoading } = useBanks({ populate: '*' });
  const { data: safes, isLoading: safesLoading } = useSafes({ populate: '*' });

  const currencyOptions: DropdownOption[] = useMemo(() => {
    if (!currencies || !Array.isArray(currencies) || currencies.length === 0) return [];
    return currencies
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({
        value: currency.documentId || currency.id || '',
        label: `${currency.code || ''} (${currency.name || currency.code || 'Unknown'})`,
      }))
      .filter((option: any) => option.value);
  }, [currencies]);

  // Combined bank + safe accounts for "money in" destination
  const accountOptions: DropdownOption[] = useMemo(() => {
    const bankOpts = (Array.isArray(banks) ? banks : [])
      .filter((bank: any) => bank.isActive ?? true)
      .map((bank: any) => ({
        value: `bank:${bank.documentId || bank.id}`,
        label: `Bank: ${bank.name || ''}${bank.accountNumber ? ` — ${bank.accountNumber}` : ''}`,
      }));
    const safeOpts = (Array.isArray(safes) ? safes : [])
      .filter((safe: any) => safe.isActive ?? true)
      .map((safe: any) => ({
        value: `safe:${safe.documentId || safe.id}`,
        label: `Safe: ${safe.name || ''}${safe.location ? ` — ${safe.location}` : ''}`,
      }));
    return [...bankOpts, ...safeOpts];
  }, [banks, safes]);

  // Server-side searchable invoice options (used by AsyncSelect).
  // Invoices are stored in the invoice-hire collection (FIN numbers).
  const fetchInvoiceOptions = useCallback(async (query: string, page: number) => {
    const response = await invoiceHiresService.getAll({
      pagination: { page, pageSize: 20 },
      sort: 'createdAt:DESC',
      ...(query ? { q: query } : {}),
    });
    return response;
  }, []);

  return {
    currencies,
    currencyOptions,
    accountOptions,
    fetchInvoiceOptions,
    isLoading: currenciesLoading || banksLoading || safesLoading,
  };
}
